// ============================================================
// TMR Portal Admin — leituras e escritas das regras de negócio, sobre @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { businessRuleKeys } from '../queryClient';
import type {
  BusinessRule,
  BusinessRuleList,
  CompanyBusinessRule,
  CompanyBusinessRules,
  RuleOverrides,
  RuleValue,
  RuleValueRequest,
} from './contract';

/** The path segment of a rule. Keys carry dots, which are fine in a path but encoded anyway. */
const rulePath = (key: string) => encodeURIComponent(key);

/**
 * Every rule with the TMR's default.
 *
 * @param enabled Whether the operator holds `business_rules.global.read` — false skips a request that would 403.
 */
export function useBusinessRules(enabled = true): UseQueryResult<BusinessRuleList> {
  return useQuery({
    queryKey: businessRuleKeys.list(),
    queryFn: ({ signal }) => api.get<BusinessRuleList>('/business-rules', { signal }),
    enabled,
  });
}

/**
 * Every rule as it applies to one Empresa.
 *
 * @param companyId The Empresa, or undefined to skip the request (no permission, or not loaded yet).
 */
export function useCompanyBusinessRules(companyId: string | undefined): UseQueryResult<CompanyBusinessRules> {
  return useQuery({
    queryKey: businessRuleKeys.company(companyId ?? ''),
    queryFn: ({ signal }) =>
      api.get<CompanyBusinessRules>(`/companies/${companyId}/business-rules`, { signal }),
    enabled: Boolean(companyId),
  });
}

/**
 * The Empresas with their own value for one rule, by name — for the sales module, the clinics released from
 * the default. Only the ones in the operator's scope.
 *
 * @param ruleKey The rule, or undefined to skip the request (no `company.business_rules.read`).
 */
export function useRuleOverrides(ruleKey: string | undefined): UseQueryResult<RuleOverrides> {
  return useQuery({
    queryKey: businessRuleKeys.overrides(ruleKey ?? ''),
    queryFn: ({ signal }) =>
      api.get<RuleOverrides>(`/business-rules/${rulePath(ruleKey ?? '')}/overrides`, { signal }),
    enabled: Boolean(ruleKey),
  });
}

/** Changes the TMR's default for a rule. The copy to the legacy follows on its own, within seconds. */
export function useUpdateRuleDefault() {
  return useBusinessRuleMutation(({ key, value }: { key: string; value: RuleValue }) =>
    api.put<BusinessRule>(`/business-rules/${rulePath(key)}`, { value } satisfies RuleValueRequest));
}

/**
 * Gives an Empresa its own value for a rule, or changes the one it has.
 *
 * @param companyId The Empresa.
 */
export function useSetCompanyRuleOverride(companyId: string) {
  return useBusinessRuleMutation(({ key, value }: { key: string; value: RuleValue }) =>
    setOverride(companyId, key, value));
}

/**
 * Takes an Empresa's own value away, so it follows the TMR's default again.
 *
 * @param companyId The Empresa.
 */
export function useRemoveCompanyRuleOverride(companyId: string) {
  return useBusinessRuleMutation((key: string) => removeOverride(companyId, key));
}

/**
 * Gives any Empresa its own value for a rule — the rule's page acts on the Empresa chosen in the list or in
 * the search, so the Empresa travels with each call instead of with the hook.
 */
export function useSetRuleOverride() {
  return useBusinessRuleMutation(({ companyId, key, value }: { companyId: string; key: string; value: RuleValue }) =>
    setOverride(companyId, key, value));
}

/** Takes any Empresa's own value away — the rule's page counterpart of {@link useRemoveCompanyRuleOverride}. */
export function useRemoveRuleOverride() {
  return useBusinessRuleMutation(({ companyId, key }: { companyId: string; key: string }) =>
    removeOverride(companyId, key));
}

function setOverride(companyId: string, key: string, value: RuleValue) {
  return api.put<CompanyBusinessRule>(
    `/companies/${companyId}/business-rules/${rulePath(key)}`,
    { value } satisfies RuleValueRequest,
  );
}

function removeOverride(companyId: string, key: string) {
  return api.delete<CompanyBusinessRule>(`/companies/${companyId}/business-rules/${rulePath(key)}`);
}

/**
 * Shared plumbing for every write: run it, then invalidate every business-rule query — a default moves what
 * every Empresa without an override shows, and an override moves the count on the global screen and the
 * rule's list of Empresas.
 *
 * @param mutationFn The request itself.
 */
function useBusinessRuleMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessRuleKeys.all });
    },
  });
}
